import { ModelType, SearchableRecordInstantSearch } from '@livv/models';
import {
    Box,
    Card,
    Link,
    Stack,
    SxProps,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { BaseHit, Hit } from 'instantsearch.js';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FC, PropsWithChildren, ReactNode, useMemo } from 'react';
import { Highlight, Snippet, useHits } from 'react-instantsearch';
import LawScopeLogo from '../ContentLogo/LawScopeLogo';
import ModelTypeLogo from '../ContentLogo/ModelTypeLogo';
import DecisionAnalysed from '../DecisionAnalysed';
import ClickablePath from './ClickablePath';
import { SecondaryAttributeData } from './SecondaryAttribute';
import SecondaryAttributes from './SecondaryAttributes';
import SubtitleAttribute from './SubtitleAttributes';
import Tags from '@components/Tags';
import useAmplitude from '@utils/hooks/useAmplitude';
import { getDisplayablePathItems } from '@utils/search/previews';
import { getSnippetedAttributes } from '@utils/search/snippet';
import { TrackingEventName } from '@utils/types/amplitude/events';

const titleSx: SxProps = {
    '&:hover': {
        textDecoration: 'underline',
    },
    '&:visited': {
        color: 'info.main',
    },
    color: 'grey.100',
    fontSize: 18,
    fontWeight: 'medium',
    textDecoration: 'none',
};

const linkTextSx: SxProps = {
    '&:hover': {
        textDecoration: 'underline',
    },
    color: 'grey.300',
    display: 'block',
    textDecoration: 'none',
};

interface CardLinkProps extends PropsWithChildren {
    dataTestId?: string;
    href: string;
    onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    style?: SxProps;
}

const CardLink: FC<CardLinkProps> = ({ children, dataTestId, href, style, onClick }) => (
    <Link
        data-test-id={dataTestId}
        href={href}
        onClick={onClick}
        rel="noopener, noreferrer"
        sx={style}
        target="_blank"
    >
        {children}
    </Link>
);

export interface PreviewCardProps<T extends BaseHit> {
    hit: Hit<T>;
}

export interface ContentCardProps<T extends BaseHit = SearchableRecordInstantSearch>
    extends PreviewCardProps<T> {
    children?: ReactNode;
    href: string;
    onClick?: () => void;
    filters?: string;
    query?: string;
    secondaryAttributes?: SecondaryAttributeData[];
    snippetAttribute?: string;
    subtitleAttributes?: SecondaryAttributeData[];
    subtitleComplement?: string;
    tags?: string[];
    titleAttribute: string;
}
const ContentCard: FC<ContentCardProps> = ({
    children,
    hit,
    href,
    onClick,
    filters,
    query,
    secondaryAttributes = [],
    snippetAttribute,
    subtitleAttributes,
    subtitleComplement,
    tags = [],
    titleAttribute,
}) => {
    const { modelType, __position: position } = hit;
    const { sendEvent } = useHits();
    const { t } = useTranslation('common');
    const { pathname } = useRouter();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'), {
        defaultMatches: true,
    });
    const { track } = useAmplitude();

    const trackSearchResultClick = () => {
        sendEvent('click', hit, 'Search Result clicked');
        track({
            name: TrackingEventName.RESULT_CLICKED,
            params: {
                result_position: position,
                result_type: modelType,
                search_context: pathname,
            },
        });
    };

    const displayedPaths = useMemo(() => getDisplayablePathItems(hit), [hit]);
    const decisionHitHasAnalysis = hit.modelType === ModelType.DECISION && hit.analyses?.length > 0;
    const hitHasNoFullTextMatch = !getSnippetedAttributes(hit).includes('fullText');

    const shouldDisplayFullText = !(decisionHitHasAnalysis && hitHasNoFullTextMatch);

    const handleClick = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        onClickOverride: (() => void) | undefined,
    ) => {
        trackSearchResultClick();
        if (onClickOverride) {
            e.preventDefault();
            onClickOverride();
        }
    };

    return (
        <Card data-test-id={`searchResult-${hit.modelType}-${hit.baseObjectID}`} elevation={0}>
            <Stack direction={{ sm: 'row', xs: 'column' }} p={2} pl={{ sm: 0, xs: 3 }}>
                <Box
                    sx={[
                        !isMobile && {
                            alignItems: 'start',
                            display: 'flex',
                            minWidth: '116px',
                            width: '116px',
                        },
                        { py: 1 },
                    ]}
                >
                    <ModelTypeLogo displayVertically={!isMobile} modelType={hit.modelType} />
                </Box>

                <Box>
                    <CardLink
                        dataTestId="searchResult-title"
                        href={href}
                        onClick={(e) => handleClick(e, onClick)}
                        style={titleSx}
                    >
                        <Highlight attribute={titleAttribute} hit={hit} />
                    </CardLink>
                    <SubtitleAttribute
                        hit={hit}
                        subtitleAttributes={subtitleAttributes || []}
                        subtitleComplement={subtitleComplement}
                    />
                    <Tags
                        hitId={hit.objectID}
                        sx={[modelType === ModelType.STATISTIC && { mb: 0, mt: 1 }]}
                        tags={
                            modelType === ModelType.GLOSSARY_TERM
                                ? [
                                      <LawScopeLogo key="lawscope" lawScope={hit.lawScopes[0]} />,
                                      ...(hit.mainKeywords || []).map((keyword) =>
                                          t(`mainKeywords:${keyword}`),
                                      ),
                                  ]
                                : tags
                        }
                    />
                    <SecondaryAttributes hit={hit} secondaryAttributes={secondaryAttributes} />
                    {displayedPaths.length > 0 && (
                        <ClickablePath hitId={hit.objectID} paths={displayedPaths} />
                    )}
                    {shouldDisplayFullText && (
                        <CardLink
                            href={href}
                            onClick={(e) => handleClick(e, onClick)}
                            style={linkTextSx}
                        >
                            <Typography
                                component="span"
                                data-test-id="searchResult-fulltext"
                                sx={{ mt: 1.5 }}
                                variant="body2"
                                gutterBottom
                            >
                                {snippetAttribute && (
                                    <Snippet attribute={snippetAttribute} hit={hit} />
                                )}
                                {children}
                            </Typography>
                        </CardLink>
                    )}

                    {/* ANALYSES CARD IF ONLY MODELTYPE IS DECISION */}
                    {modelType === ModelType.DECISION && (
                        <DecisionAnalysed filters={filters} hit={hit} query={query} />
                    )}
                </Box>
            </Stack>
        </Card>
    );
};

export default ContentCard;
